// dependencies
import React, { Fragment } from "react";
import { View } from "react-native";
import { useIntl } from "react-intl";

// components
import Jumbotron from "@cloudspire/legacy-resources/src/components/Jumbotron";
import { Row, Col } from "@cloudspire/legacy-resources/src/components/Grid";
import Button from "@cloudspire/legacy-resources/src/components/Button2";
import Stack from "@cloudspire/legacy-resources/src/components/Stack";
import Heading from "@cloudspire/legacy-resources/src/components/Heading";
import Field from "@cloudspire/legacy-resources/src/components/Field";
import { IconMailO } from "@cloudspire/legacy-svg-icon-library";
import Text from "@cloudspire/legacy-resources/src/components/Text";
import Form from "@cloudspire/legacy-resources/src/components/Form";
import Select from "@cloudspire/legacy-resources/src/components/Select2";
import Splash from "@cloudspire/legacy-resources/src/components/Splash";
import TextInput from "@cloudspire/legacy-resources/src/components/TextInput2";
import RecaptchaMessage from "@cloudspire/legacy-resources/src/components/RecaptchaMessage";

// containers
import BookingDatePickerWithTextInput from "@cloudspire/legacy-resources/src/containers/BookingDatePickerWithTextInput";

// constants
import theming from "@cloudspire/legacy-resources/src/constants/theming";
import {
  CONTACT_TYPE_INTL_MESSAGES,
  CONTACT_TYPE_LIST,
} from "@cloudspire/shared/constants/contact";
import {
  REQUEST_STATUS_VALUE_SUCCESS,
  REQUEST_STATUS_VALUE_FETCHING,
} from "@cloudspire/legacy-resources/src/constants/request";
import { apiRouter } from "@cloudspire/legacy-shared/src/constants/router";

// contexts
import { useConfiguration } from "@cloudspire/legacy-resources/src/contexts/configuration";
import { useRouters } from "@cloudspire/legacy-resources/src/contexts/routers";

// libraries
import { emToPx } from "@cloudspire/legacy-resources/src/libraries";
import { get } from "@cloudspire/legacy-shared/src/libraries";
import {
  fromDate,
  fromString,
} from "@cloudspire/legacy-resources/src/libraries/EnhancedDate";
import intlFormatConstantByValue from "@cloudspire/shared/utils/intl/formatConstantByValue";

const { useTheme } = theming;

function ContactForm(props) {
  const { parameterList, status, error, onChange, onSubmit } = props;

  const intl = useIntl();

  const theme = useTheme();

  const { frontRouter } = useRouters();

  const configuration = useConfiguration();

  return (
    <View
      style={{
        display: "flex",
        backgroundColor: "#ffffff",
        padding: emToPx(1),
        borderRadius: emToPx(0.25),
        shadowColor: "#000000",
        shadowOffset: { width: 0, height: 0 },
        shadowOpacity: 1,
        shadowRadius: 12,
      }}
    >
      <Jumbotron
        icon={<IconMailO />}
        title={
          <Heading level={1} size="lg">
            {intl.formatMessage({
              defaultMessage: "Nous contacter",
            })}
          </Heading>
        }
        subtitle={
          <Heading level={2} size="sm">
            {intl.formatMessage({
              defaultMessage: "Envoyez nous un message pour demander de l’aide",
            })}
          </Heading>
        }
      />
      {status & REQUEST_STATUS_VALUE_SUCCESS ? (
        <Fragment>
          <View dataSet={{ gtm: "contact-submit-success" }} />

          <Splash splashSource={{ uri: "/images/splashes/contact-sent.png" }} />

          <Stack marginTop={1} />

          <Text style={{ textAlign: "center" }}>
            {intl.formatMessage({
              defaultMessage:
                "Votre message nous a été transmis avec succès, nous vous répondrons dans les plus brefs délais.",
            })}
          </Text>

          <Stack marginTop={1} />

          <View style={{ justifyContent: "center" }}>
            <Button
              label={intl.formatMessage({
                defaultMessage: "Retourner à l’accueil",
              })}
              to={frontRouter
                .findByName("App.Action.Home")
                .toFilled()
                .setParameter("lang", intl.locale)
                .generateUri()
                .toString()}
              color="brand"
            >
              {intl.formatMessage({
                defaultMessage: "Retourner à l’accueil",
              })}
            </Button>
          </View>
        </Fragment>
      ) : (
        <Form
          method="post"
          encType="multipart/form-data"
          action={apiRouter
            .findByName("App.Contact.Create")
            .toFilled()
            .generateUri()
            .toString()}
          onSubmit={onSubmit}
        >
          <Stack marginTop={1} />

          {error?.message && (
            <>
              <Text style={{ color: "#ff0000" }}>{get(error, "message")}</Text>

              <Stack marginTop={1} />
            </>
          )}

          <Heading
            level={2}
            size="lg"
            style={{
              color: theme.SECONDARY_COLOR,
            }}
          >
            {intl.formatMessage({
              defaultMessage: "Besoin d’aide ou d’un renseignement ?",
            })}
          </Heading>

          <Stack marginTop={1} />

          <Field errorList={get(error, "list.type", [])} required>
            <Select
              name="type"
              required={true}
              value={get(parameterList, "type", "")}
              optionList={CONTACT_TYPE_LIST.map((type) => ({
                id: type.id,
                value: type.id,
                label: intlFormatConstantByValue(
                  {
                    intl,
                    messages: CONTACT_TYPE_INTL_MESSAGES.byValue,
                    preferredAttributes: ["label"],
                    value: type.id,
                  },
                  {
                    uppercaseFirst: true,
                  }
                ),
              }))}
              placeholder={intl.formatMessage({
                defaultMessage: "Je suis…",
              })}
              onChange={onChange}
            />
          </Field>

          <Stack marginTop={1} />

          {configuration.channel.website.useBookingDelegation && (
            <Fragment>
              <Field
                label={intl.formatMessage({
                  defaultMessage: "Canal de vente",
                })}
                errorList={error?.list?.type ?? []}
                required
              >
                <Select
                  name="channelId"
                  required={true}
                  disabled={Boolean(parameterList?.roomReference)}
                  value={parameterList?.channelId ?? ""}
                  optionList={Object.entries(
                    configuration.channel.website.delegatedChannels
                  ).map(([channelId, channel]) => {
                    return {
                      id: channelId,
                      value: channelId,
                      label: channel.name,
                    };
                  })}
                  placeholder={intl.formatMessage({
                    defaultMessage: "Je contacte…",
                  })}
                  onChange={onChange}
                />
              </Field>

              <Stack marginTop={1} />
            </Fragment>
          )}

          <Row
            breakpointList={{ sm: 450, md: Infinity }}
            col={{ sm: 1, md: 2 }}
            gutter={0.5}
          >
            <Col>
              <Field
                label={intl.formatMessage({
                  defaultMessage: "Votre prénom",
                })}
                errorList={get(error, "list.firstname", [])}
              >
                <TextInput
                  name="firstname"
                  id="formFirstname"
                  required={true}
                  label={intl.formatMessage({
                    defaultMessage: "Prénom",
                  })}
                  value={get(parameterList, "firstname", "")}
                  onChange={onChange}
                />
              </Field>
            </Col>

            <Col>
              <Field
                label={intl.formatMessage({
                  defaultMessage: "Votre nom de famille",
                })}
                errorList={get(error, "list.lastname", [])}
              >
                <TextInput
                  name="lastname"
                  id="formLastname"
                  required={true}
                  label={intl.formatMessage({
                    defaultMessage: "Nom de famille",
                  })}
                  value={get(parameterList, "lastname", "")}
                  onChange={onChange}
                />
              </Field>
            </Col>
          </Row>

          <Stack marginTop={1} />

          <View>
            <Field
              label={intl.formatMessage({
                defaultMessage: "Votre numéro de téléphone",
              })}
              errorList={get(error, "list.phone", [])}
            >
              <TextInput
                name="phone"
                id="formPhone"
                label={intl.formatMessage({
                  defaultMessage: "Numéro de téléphone",
                })}
                value={get(parameterList, "phone", "")}
                onChange={onChange}
              />
            </Field>
          </View>

          <Stack marginTop={1} />

          <View>
            <Field
              label={intl.formatMessage({
                defaultMessage: "Votre adresse e-mail",
              })}
              errorList={get(error, "list.email", [])}
            >
              <TextInput
                name="email"
                id="formEmail"
                required={true}
                label={intl.formatMessage({
                  defaultMessage: "Adresse e-mail",
                })}
                value={get(parameterList, "email", "")}
                onChange={onChange}
              />
            </Field>
          </View>

          <Stack marginTop={1} />

          <View>
            <Field
              label={intl.formatMessage({
                defaultMessage: "Sujet de votre message",
              })}
              errorList={get(error, "list.subject", [])}
            >
              <TextInput
                name="subject"
                id="formSubject"
                required={true}
                label={intl.formatMessage({
                  defaultMessage: "Sujet du message",
                })}
                value={get(parameterList, "subject", "")}
                onChange={onChange}
              />
            </Field>
          </View>

          <Stack marginTop={1} />

          {get(parameterList, "roomReference") && (
            <>
              <Row
                breakpointList={{ sm: 450, md: Infinity }}
                col={{ sm: 1, md: 2 }}
                gutter={0.5}
              >
                <Col>
                  <Field
                    label={intl.formatMessage({
                      defaultMessage: "Référence de l’hébergement",
                    })}
                  >
                    <TextInput
                      name="roomReference"
                      id="formRoomReference"
                      readOnly={true}
                      label={intl.formatMessage({
                        defaultMessage: "Référence de l’hébergement",
                      })}
                      value={get(parameterList, "roomReference", "")}
                      onChange={onChange}
                    />
                  </Field>
                </Col>

                <Col>
                  <Field
                    label={intl.formatMessage({
                      defaultMessage: "Nombre de personne",
                    })}
                    errorList={get(error, "list.capacity", [])}
                  >
                    <TextInput
                      type="number"
                      name="capacity"
                      id="formCapacity"
                      label={intl.formatMessage({
                        defaultMessage: "Nombre de personne",
                      })}
                      value={get(parameterList, "capacity", "")}
                      onChange={onChange}
                    />
                  </Field>
                </Col>

                <Col span={{ sm: 1, md: 2 }}>
                  <BookingDatePickerWithTextInput
                    name="checkinCheckout"
                    roomReference={get(parameterList, "roomReference")}
                    channelId={configuration.channel.id}
                    start={
                      fromString(
                        get(parameterList, "checkin", null),
                        "Y-m-d"
                      ) || null
                    }
                    end={
                      fromString(
                        get(parameterList, "checkout", null),
                        "Y-m-d"
                      ) || null
                    }
                    initialDate={fromDate()}
                    min={fromDate().startOfDays()}
                    formatDateOptions={{
                      month: "2-digit",
                      day: "2-digit",
                      year: "2-digit",
                    }}
                    onSelect={onChange}
                  />
                </Col>
              </Row>

              <Stack marginTop={1} />
            </>
          )}

          <View>
            <Field
              label={intl.formatMessage({
                defaultMessage: "Contenu de votre message",
              })}
              errorList={get(error, "list.message", [])}
            >
              <TextInput
                name="message"
                type="multiline"
                id="formMessage"
                required={true}
                label={intl.formatMessage({
                  defaultMessage: "Contenu du message",
                })}
                numberOfLines={3}
                value={get(parameterList, "message", "")}
                onChange={onChange}
              />
            </Field>
          </View>

          <Stack marginTop={1} />

          <View style={{ alignItems: "flex-end" }}>
            <Button
              disabled={REQUEST_STATUS_VALUE_FETCHING === status}
              type="submit"
              appearance="secondary"
              label={intl.formatMessage({
                defaultMessage: "Envoyer mon message",
              })}
            />
          </View>

          <Stack marginTop={1} />

          <RecaptchaMessage />
        </Form>
      )}
    </View>
  );
}

export default ContactForm;
